<template>
	<div class="flex flex-col w-[calc(100%-60px)] sm:max-w-[920px] md:max-w-[1400px] items-center mx-[30px]">
		<div
			class="relative flex flex-col items-center bg-white border border-dj-gray-light rounded-[30px] w-full py-5 md:py-0"
		>
			<!-- searchbar -->
			<div
				v-if="loaded"
				class="flex flex-col md:flex-row md:items-center min-h-[100px] w-full pl-5 md:pl-4 pr-5 md:pr-0"
			>
				<div class="flex flex-col md:flex-row md:items-center w-full" v-show="searchbarLoaded">
					<SearchbarJobsAutocomplete
						v-model="autocomplete"
						:placeholder="$t('jobs.searchbar.search.placeholder')"
						:hits-per-page="30"
						@loaded="searchbarLoading.autocomplete = true"
						@submit="runSearch"
					/>
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsCategory
						ref="categoriesRef"
						v-model="categories"
						:placeholder="$t('jobs.searchbar.category.placeholder')"
						@loaded="searchbarLoading.category = true"
					/>
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsLocation
						v-if="loaded && Object.keys(filters).length > 0 && Object.keys(filters.country).length > 0"
						ref="locationsRef"
						v-model="locations"
						:filter-list="filters.country.data"
						:placeholder="$t('jobs.searchbar.location.placeholder')"
						@loaded="searchbarLoading.location = true"
					/>
					<div v-else class="relative flex w-full md:w-[calc(20%-20px)]"></div>
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsType
						ref="typesRef"
						v-model="types"
						:placeholder="$t('jobs.searchbar.type.placeholder')"
						@loaded="searchbarLoading.type = true"
					/>
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsExtras
						v-model="extras"
						:placeholder="$t('jobs.searchbar.extras.placeholder')"
						:extras-data="filters?.badge?.data || []"
						@loaded="searchbarLoading.extra = true"
					/>
					<div
						@click="runSearch"
						class="hidden md:flex justify-center items-center mt-2 md:mt-0 ml-auto md:ml-4 mr-auto md:mr-0 w-full sm:w-[200px] md:w-dj-60 h-dj-60 text-white bg-dj-blue rounded-normal cursor-pointer"
					>
						<BaseIcon class="text-[24px] inline-block" icon="ic:outline-search" color="white" />
					</div>
				</div>
			</div>
			<div
				v-else
				class="flex items-center min-h-[100px] rounded-[30px] w-full bg-dj-gray-minimal animate-pulse"
			></div>
			<!-- detailed searchbar -->
			<div v-show="showDetailedSearch" class="flex flex-col md:h-[110px] w-full pl-5 md:pl-4 pr-5 md:pr-4">
				<div class="hidden md:flex h-[1px] bg-dj-gray-light"></div>
				<div class="flex flex-col md:flex-row items-center w-full h-full">
					<SearchbarJobsDetailedLevel
						v-model="detailedLevel"
						:placeholder="$t('jobs.detailedSearchbar.level.placeholder')"
					/>
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsDetailedSalary v-model="detailedSalary" />
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsDetailedNewJobs v-model="detailedNewJob" />
					<div class="hidden md:flex divider"></div>
					<SearchbarJobsDetailedExpiringJobs v-model="detailedExpiringJobs" />
				</div>
			</div>
			<div
				@click="runSearch"
				class="flex md:hidden justify-center items-center mt-2 md:mt-0 ml-5 sm:ml-auto md:ml-4 mr-5 sm:mr-auto md:mr-0 w-[calc(100%-40px)] sm:w-[200px] md:w-dj-60 h-dj-60 text-white bg-dj-blue rounded-normal cursor-pointer"
			>
				<BaseIcon class="text-[24px] inline-block" icon="ic:outline-search" color="white" />
				<span class="ml-2">{{ $t("search") }}</span>
			</div>
		</div>
		<div class="relative flex flex-col md:flex-row items-center w-full mt-5">
			<div
				@click="showDetailedSearch = !showDetailedSearch"
				class="relative md:absolute top-0 md:left-1/2 md:-translate-x-1/2 flex items-center cursor-pointer"
			>
				<span class="text-xs">
					<template v-if="showDetailedSearch == true">{{ $t("jobs.searchbar.basicSearch") }}</template>
					<template v-else>{{ $t("jobs.searchbar.detailedSearch") }}</template>
				</span>
				<BaseIcon
					:icon="showDetailedSearch ? 'ic:outline-expand-less' : 'ic:outline-expand-more'"
					class="ml-1 text-[24px]"
				/>
			</div>
			<BaseButton
				@click="resetSearch"
				class="md:ml-auto mt-5 md:mt-0 text-dj-gray"
				type="secondary"
				size="small"
				rounded
			>
				{{ $t("jobs.reset") }}
			</BaseButton>
			<NuxtLink :to="isAuth ? `/${locale}/user/notifications` : `/${locale}/jobnotification`" target="_blank">
				<BaseButton class="ml-2 mt-5 md:mt-0" type="secondary-red" size="small" rounded>
					<BaseIcon icon="mdi:bell-outline" color="#E1415A" class="mr-[10px] text-[24px]" />
					{{ $t("user.notifications.create") }}
				</BaseButton>
			</NuxtLink>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from "vue";
import { type filtersResponse, type locationObject } from "@@/types";

export default defineComponent({
	name: "SearchbarJobs",
	props: {
		navigateToLocationPath: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["runSearch", "fieldsData"],
	async setup(props, { emit }) {
		type CountryWithChildren = {
			children?: locationObject[];
		};

		const { locale } = useI18n();
		const route = useRoute();
		const router = useRouter();
		const isAuth = useState("isAuth");

		const autocomplete = ref(route.query.keyword ? String(route.query.keyword) : "");

		const categories = ref({
			categories: route.query.category ? String(route.query.category).split(",") : [],
			subcategories: route.query.subcategory ? String(route.query.subcategory).split(",") : [],
		});

		const types = ref<number[]>(route.query.type ? String(route.query.type).split(",").map(Number) : []);
		const extras = ref<number[]>(
			route.query.extra
				? String(route.query.extra)
						.split(",")
						.map((id) => Number(id))
				: []
		);

		// Detailed search hydration
		const detailedLevel = ref<number[]>(route.query.level ? String(route.query.level).split(",").map(Number) : []);
		const detailedSalary = ref(route.query.salary === "true");
		const detailedNewJob = ref(route.query.new_job === "true");
		const detailedExpiringJobs = ref(route.query.expiring === "true");

		// Locations: Assuming child component expects array of objects with IDs
		const locations = ref<locationObject[]>(
			route.query.location
				? String(route.query.location)
						.split(",")
						.map((id) => ({ id: Number(id) } as locationObject))
				: []
		);

		// Check detailed values immediately based on hydrated state
		const showDetailedSearch = ref(
			detailedSalary.value || detailedNewJob.value || detailedExpiringJobs.value || detailedLevel.value.length > 0
		);

		// Searchbar loading state (for child component initialization)
		const searchbarLoading = ref({
			autocomplete: false,
			category: false,
			location: false,
			type: false,
			extra: false,
		});

		const searchbarLoaded = computed(() => {
			return Object.values(searchbarLoading.value).every(Boolean);
		});

		const categoriesRef = ref();
		const locationsRef = ref();
		const typesRef = ref();

		const { data: fetchedFilters, pending } = await useFetch<filtersResponse>(
			() => `${globalConfig.API}/filters/job?region=${useRegion.getCurrentRegion().code.toLowerCase()}`,
			{
				server: true,
				default: () => ({}),
			}
		);

		const filters = computed(() => fetchedFilters.value);
		const loaded = computed(() => !pending.value);

		if (!route.query.location && route.params.citySlug) {
			const normalizedCitySlug = String(route.params.citySlug).replace(/-\d+$/, "");
			const countries = (fetchedFilters.value?.country?.data || []) as CountryWithChildren[];
			let matchedLocation: locationObject | null = null;
			for (const country of countries) {
				const found = (country.children || []).find((loc) => loc.slug === normalizedCitySlug);
				if (found) {
					matchedLocation = found;
					break;
				}
			}

			if (matchedLocation) {
				locations.value = [matchedLocation];
			}
		}

		function checkDetailedValues() {
			if (
				detailedSalary.value ||
				detailedNewJob.value ||
				detailedExpiringJobs.value ||
				detailedLevel.value.length > 0
			) {
				showDetailedSearch.value = true;
			}
		}

		function emitFieldsData() {
			// Added safety checks in case child refs aren't fully mounted yet
			const fieldsData = [
				...(categoriesRef.value?.getSelectedCategories?.() || []),
				...(locationsRef.value?.getSelectedLocations?.() || []),
				...(typesRef.value?.getSelectedTypes?.() || []),
			];

			if (autocomplete.value.length > 0) fieldsData.unshift(autocomplete.value);

			if (fieldsData.length > 0) emit("fieldsData", fieldsData);
		}

		watch(
			() => route.query,
			(query) => {
				if (route.path === `/${locale.value}/jobs` && Object.keys(query).length > 0) {
					checkDetailedValues();
					nextTick(() => {
						emitFieldsData();
					});
				}
			},
			{ immediate: true }
		); // immediate triggers it on first load too

		function runSearch() {
			let singleLocationSlug: string | null = null;

			if (props.navigateToLocationPath && locations.value.length === 1) {
				const selectedLocation = locations.value[0] as Partial<locationObject>;
				singleLocationSlug = selectedLocation.slug || null;

				if (!singleLocationSlug && selectedLocation.id) {
					const selectedId = Number(selectedLocation.id);
					const countries = (fetchedFilters.value?.country?.data || []) as CountryWithChildren[];
					const availableLocations = countries.flatMap((country) => country.children || []);
					const matchedLocation = availableLocations.find((location) => Number(location.id) === selectedId);
					singleLocationSlug = matchedLocation?.slug || null;
				}
			}

			const query = {
				...(autocomplete.value.length > 0 && { keyword: autocomplete.value }),
				...(categories.value.categories.length > 0 && { category: categories.value.categories.join(",") }),
				...(categories.value.subcategories.length > 0 && {
					subcategory: categories.value.subcategories.join(","),
				}),
				...(locations.value.length > 0 &&
					!singleLocationSlug && { location: locations.value.map((l) => l.id).join(",") }),
				...(types.value.length > 0 && { type: types.value.join(",") }),
				...(extras.value.length > 0 && { extra: extras.value.join(",") }),
				...(detailedLevel.value.length > 0 && { level: detailedLevel.value.join(",") }),
				...(detailedSalary.value && { salary: detailedSalary.value.toString() }),
				...(detailedNewJob.value && { new_job: detailedNewJob.value.toString() }),
				...(detailedExpiringJobs.value && { expiring: detailedExpiringJobs.value.toString() }),
			};

			const path = singleLocationSlug ? `/${locale.value}/jobs/${singleLocationSlug}` : `/${locale.value}/jobs`;

			router.push({ path, query }).then(() => {
				emit("runSearch");
				emitFieldsData();
			});
		}

		function resetSearch() {
			// Reset values
			autocomplete.value = "";
			categories.value = { categories: [], subcategories: [] };
			locations.value = [];
			types.value = [];
			extras.value = [];
			detailedLevel.value = [];
			detailedSalary.value = false;
			detailedNewJob.value = false;
			detailedExpiringJobs.value = false;

			showDetailedSearch.value = false;

			router.push(`/${locale.value}/jobs`);
		}

		return {
			showDetailedSearch,
			autocomplete,
			categories,
			locations,
			types,
			extras,
			detailedSalary,
			detailedNewJob,
			detailedExpiringJobs,
			detailedLevel,
			runSearch,
			filters,
			loaded,
			resetSearch,
			categoriesRef,
			locationsRef,
			typesRef,
			emitFieldsData,
			searchbarLoading,
			searchbarLoaded,
			locale,
			isAuth,
		};
	},
});
</script>

<style>
.divider {
	@apply w-[1px] bg-dj-gray-light py-[30px];
}
</style>
